<template>
  <div class="rpt-page">
    <div class="rpt-head">
      <div>
        <div class="rpt-crumb">Reportes</div>
        <h2 class="rpt-title">Cartera pendiente de liquidación</h2>
        <p class="rpt-subtitle">
          Facturas recibidas que aún no se liquidan, con su antigüedad y el
          monto CIF que representan. Excluye las rechazadas, que se revisan en
          el reporte de rechazos.
        </p>
      </div>
      <div class="rpt-actions">
        <v-btn
          icon
          to="/reportes/guia"
          title="Cómo leer este reporte"
          class="rpt-help"
        >
          <v-icon>help_outline</v-icon>
        </v-btn>
        <v-btn
          class="rpt-btn--ghost"
          depressed
          :disabled="!detalle.length"
          @click="exportar"
        >
          <v-icon left small>file_download</v-icon>Exportar
        </v-btn>
        <v-btn class="rpt-btn" depressed :loading="loading" @click="load">
          <v-icon left small>refresh</v-icon>Actualizar
        </v-btn>
      </div>
    </div>

    <v-layout row wrap class="rpt-filters">
      <v-flex md4 sm6 xs12>
        <v-select
          :items="laboratorioItems"
          v-model="labId"
          label="Laboratorio"
          prepend-icon="business"
          clearable
        ></v-select>
      </v-flex>
      <v-flex md4 sm6 xs12 v-if="!isDistributor">
        <v-select
          :items="distribuidorItems"
          v-model="distId"
          label="Distribuidora"
          prepend-icon="local_shipping"
          clearable
        ></v-select>
      </v-flex>
      <v-flex md4 sm6 xs12>
        <v-select
          :items="limitOptions"
          v-model="limit"
          label="Filas de detalle"
          prepend-icon="list"
        ></v-select>
      </v-flex>
    </v-layout>

    <!-- Cartera muy antigua casi siempre significa facturas que se liquidaron
         en la práctica pero nunca se marcaron. Se avisa para que no se lea
         como deuda real. -->
    <v-alert
      v-if="resumen && resumen.avgEdad > 180"
      :value="true"
      color="#E6C57D"
      icon="info"
      class="rpt-alert"
    >
      La antigüedad promedio es de {{ format(resumen.avgEdad) }} días. Una
      cartera tan antigua suele indicar facturas ya resueltas que nunca se
      marcaron como liquidadas: conviene depurar antes de leer el monto como
      pendiente real.
    </v-alert>

    <v-layout row wrap v-if="resumen">
      <v-flex lg4 sm6 xs12>
        <div class="rpt-tile rpt-tile--hero">
          <span class="rpt-tile__label">Monto pendiente (CIF)</span>
          <span class="rpt-tile__value">{{ money(resumen.cifTotal) }}</span>
          <span class="rpt-tile__foot">
            {{ resumen.n }} facturas sin liquidar
          </span>
        </div>
      </v-flex>

      <v-flex lg2 sm6 xs12>
        <div class="rpt-tile">
          <span class="rpt-tile__label">Antigüedad promedio</span>
          <span class="rpt-tile__value">
            {{ format(resumen.avgEdad) }}<small>días</small>
          </span>
          <span class="rpt-tile__foot">
            la más antigua: {{ format(resumen.maxEdad) }} días
          </span>
        </div>
      </v-flex>

      <v-flex lg2 sm6 xs12>
        <div class="rpt-tile">
          <span class="rpt-tile__label">Más de 60 días</span>
          <span class="rpt-tile__value">
            {{ resumen.mayores60 }}<small>facturas</small>
          </span>
          <span class="rpt-tile__foot">
            {{ resumen.pctMayores60 === null ? '—' : resumen.pctMayores60 }}% de
            la cartera
          </span>
        </div>
      </v-flex>

      <v-flex lg2 sm6 xs12>
        <div class="rpt-tile">
          <span class="rpt-tile__label">Valor a más de 60 días</span>
          <span class="rpt-tile__value">{{ money(resumen.cifMayores60) }}</span>
          <span class="rpt-tile__foot">expuesto en el tramo más viejo</span>
        </div>
      </v-flex>

      <v-flex lg2 sm6 xs12>
        <div class="rpt-tile">
          <span class="rpt-tile__label">Aún sin salir de F&amp;D</span>
          <span class="rpt-tile__value">
            {{ resumen.sinSalida }}<small>en curso</small>
          </span>
          <span class="rpt-tile__foot">sin fecha de salida registrada</span>
        </div>
      </v-flex>
    </v-layout>

    <!-- Antigüedad: el reparto del dinero entre tramos es la lectura principal. -->
    <v-layout row wrap>
      <v-flex lg5 sm12 xs12 v-if="chartData">
        <v-widget content-bg="white" title="Monto pendiente por antigüedad">
          <div slot="widget-content">
            <e-chart
              :path-option="chartOptions"
              width="90%"
              height="300px"
            ></e-chart>
          </div>
        </v-widget>
      </v-flex>

      <v-flex lg7 sm12 xs12>
        <v-widget content-bg="white" title="Concentración por cliente">
          <div slot="widget-content">
            <div class="rpt-scroll">
              <table class="rpt-table">
                <thead>
                  <tr>
                    <th>Laboratorio</th>
                    <th>Distribuidora</th>
                    <th class="num">Facturas</th>
                    <th class="num">CIF pendiente</th>
                    <th class="num">Días prom.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(c, i) in porCliente" :key="i">
                    <td>{{ c.laboratorio }}</td>
                    <td class="rpt-muted">{{ c.distribuidor }}</td>
                    <td class="num">{{ c.n }}</td>
                    <td class="num rpt-strong">{{ money(c.cif) }}</td>
                    <td class="num">
                      <span
                        :class="[
                          'rpt-pill',
                          c.avgEdad > 60 ? 'rpt-pill--warn' : '',
                        ]"
                      >
                        {{ format(c.avgEdad) }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!porCliente.length">
                    <td colspan="5" class="rpt-empty">
                      No hay cartera pendiente con los filtros aplicados.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </v-widget>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex lg12 sm12 xs12>
        <v-widget content-bg="white" title="Detalle de facturas pendientes">
          <div slot="widget-content">
            <div class="rpt-scroll">
              <table class="rpt-table">
                <thead>
                  <tr>
                    <th># Factura</th>
                    <th># Control</th>
                    <th>Laboratorio</th>
                    <th>Distribuidora</th>
                    <th class="num">CIF</th>
                    <th class="num">Recibida</th>
                    <th class="num">Días</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="f in detalle" :key="f.id">
                    <td class="rpt-strong">{{ f.invoiceNumber }}</td>
                    <td class="rpt-muted">{{ f.controlNumber || '—' }}</td>
                    <td>{{ f.laboratorio || '—' }}</td>
                    <td class="rpt-muted">{{ f.distribuidor || '—' }}</td>
                    <td class="num">{{ money(f.cif) }}</td>
                    <td class="num rpt-muted">{{ date(f.receivedDate) }}</td>
                    <td class="num">
                      <span
                        :class="[
                          'rpt-pill',
                          f.diasEdad > 60 ? 'rpt-pill--warn' : '',
                        ]"
                      >
                        {{ f.diasEdad }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!detalle.length">
                    <td colspan="7" class="rpt-empty">Sin resultados.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p
              v-if="meta && meta.truncado"
              class="rpt-muted"
              style="font-size:12px;margin:10px 2px 0"
            >
              Se muestran las {{ meta.limite }} facturas más antiguas. Suba el
              límite de filas para ver más.
            </p>
          </div>
        </v-widget>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import EChart from '@/components/chart/echart'
import VWidget from '@/components/VWidget'
import exportReport from '@/helpers/ReportExport'
import ReportFilters from './mixins/ReportFilters'

// Rampa secuencial: a más antigüedad, más saturado. El tramo 60+ cae en el
// terracota de alerta que ya usa el resto del sistema.
const COLORS = ['#8FB6E0', '#4A80BA', '#254F87', '#BD614D']

export default {
  components: { VWidget, EChart },

  mixins: [ReportFilters],

  data() {
    return {
      limit: 100,
      limitOptions: [50, 100, 250, 500],
      meta: null,
      resumen: null,
      aging: [],
      porCliente: [],
      detalle: [],
      chartData: null,
      loading: false,
    }
  },

  computed: {
    chartOptions() {
      return [
        ['dataset.source', this.chartData],
        ['color', ['#254F87']],
        ['legend.show', false],
        ['grid.left', 70],
        ['grid.right', 24],
        ['grid.top', 16],
        ['grid.bottom', 32],
        ['tooltip.trigger', 'axis'],
        ['tooltip.axisPointer.type', 'shadow'],
        ['xAxis.type', 'category'],
        ['xAxis.name', 'días'],
        // El wrapper oculta las etiquetas de eje por defecto: hay que pedirlas.
        ['xAxis.axisLabel.show', true],
        ['xAxis.axisTick.show', false],
        ['xAxis.axisLine.lineStyle.color', '#e2e8f0'],
        ['yAxis.type', 'value'],
        ['yAxis.name', 'CIF'],
        ['yAxis.nameTextStyle.color', '#64748b'],
        ['yAxis.axisLabel.show', true],
        ['yAxis.axisLine.show', false],
        ['yAxis.axisTick.show', false],
        ['yAxis.splitLine.lineStyle.color', '#eef2f7'],
        ['series[0].type', 'bar'],
        ['series[0].barMaxWidth', 46],
        ['series[0].itemStyle.barBorderRadius', [4, 4, 0, 0]],
        // El color por barra codifica la severidad del tramo.
        [
          'series[0].itemStyle.color',
          params => COLORS[params.dataIndex] || COLORS[0],
        ],
      ]
    },
  },

  watch: {
    labId() {
      this.load()
    },
    distId() {
      this.load()
    },
    limit() {
      this.load()
    },
  },

  created() {
    this.loadCatalogos()
    this.load()
  },

  methods: {
    load() {
      // e-chart lee sus opciones una sola vez al montar: hay que desmontarlo
      // para que el v-if lo vuelva a crear con los datos nuevos.
      this.chartData = null
      this.loading = true

      this.$store
        .dispatch(
          'reports/getCarteraPendiente',
          this.params({ limit: this.limit })
        )
        .then(response => {
          const data = response && response.data
          if (!data) return
          this.meta = data.meta
          this.resumen = data.resumen
          this.aging = data.aging || []
          this.porCliente = data.porCliente || []
          this.detalle = data.detalle || []
          this.chartData = this.aging.map(row => ({
            tramo: row.bucket,
            CIF: row.cif,
          }))
        })
        .finally(() => {
          this.loading = false
        })
    },

    exportar() {
      exportReport('cartera-pendiente', [
        {
          name: 'Detalle',
          rows: this.detalle,
          columns: [
            { key: 'invoiceNumber', label: 'Factura' },
            { key: 'controlNumber', label: 'Control' },
            { key: 'laboratorio', label: 'Laboratorio' },
            { key: 'distribuidor', label: 'Distribuidora' },
            { key: 'cif', label: 'CIF' },
            { key: 'receivedDate', label: 'Recibida', format: this.date },
            { key: 'admissionDate', label: 'Admisión', format: this.date },
            { key: 'departureDate', label: 'Salida F&D', format: this.date },
            { key: 'diasEdad', label: 'Días pendiente' },
            { key: 'bucket', label: 'Tramo' },
          ],
        },
        {
          name: 'Por cliente',
          rows: this.porCliente,
          columns: [
            { key: 'laboratorio', label: 'Laboratorio' },
            { key: 'distribuidor', label: 'Distribuidora' },
            { key: 'n', label: 'Facturas' },
            { key: 'cif', label: 'CIF pendiente' },
            { key: 'avgEdad', label: 'Días promedio' },
            { key: 'maxEdad', label: 'Días máximo' },
          ],
        },
        {
          name: 'Antigüedad',
          rows: this.aging,
          columns: [
            { key: 'bucket', label: 'Tramo (días)' },
            { key: 'n', label: 'Facturas' },
            { key: 'cif', label: 'CIF' },
            { key: 'avgEdad', label: 'Días promedio' },
          ],
        },
      ])
    },
  },
}
</script>
